/**
 * ActionDetails component - Displays details about the PTO suggestion
 * Shows team conflicts and energy prediction factors
 */

import React, { useRef, useCallback } from 'react'
import { Typography, Tooltip } from 'antd'
import { PiCheckBold, PiWarningBold, PiUsersLight } from 'react-icons/pi'
import { useIntl, type IntlShape } from 'react-intl'
import { EnergyPredictionFactorType } from '@vacationtracker/shared/types/user-insight'
import { PTOSuggestion } from '../../../types'

const { Text } = Typography

interface ActionDetailsProps {
  suggestion: PTOSuggestion
  onTooltipView?: (
    tooltipType: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode',
    tooltipContent: string,
    elementHovered: string,
    section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown',
    hoverDurationMs: number
  ) => void
}

/**
 * Translates energy prediction factor type to localized message
 */
function translateEnergyPredictionFactor(factor: EnergyPredictionFactorType, formatMessage: (descriptor: { id: string }) => string): string {
  const factorMap: Record<EnergyPredictionFactorType, string | null> = {
    // Do not show NO_TEAM_CONFLICTS, because it has it's own handling
    [EnergyPredictionFactorType.NO_TEAM_CONFLICTS]: null, // 'energyLevel.energyPredictionFactor.noTeamConflicts',
    [EnergyPredictionFactorType.EXTENDED_VACATION_WITH_HOLIDAYS]: 'energyLevel.energyPredictionFactor.extendedVacationWithHolidays',
    [EnergyPredictionFactorType.VACATION_INCLUDES_HOLIDAYS]: null, //'energyLevel.energyPredictionFactor.vacationIncludesHolidays',
    [EnergyPredictionFactorType.EXTENDED_WEEKEND]: 'energyLevel.energyPredictionFactor.extendedWeekend',
    [EnergyPredictionFactorType.WEEK_PLUS_VACATION]: null, // 'energyLevel.energyPredictionFactor.weekPlusVacation',
    [EnergyPredictionFactorType.MINIMAL_TEAM_CONFLICTS]: 'energyLevel.energyPredictionFactor.minimalTeamConflicts',
    [EnergyPredictionFactorType.SOME_TEAM_CONFLICTS]: 'energyLevel.energyPredictionFactor.someTeamConflicts',
    [EnergyPredictionFactorType.SHORT_BREAK]: 'energyLevel.energyPredictionFactor.shortBreak',
    [EnergyPredictionFactorType.ESTABLISHED_WORK_PATTERNS]: 'energyLevel.energyPredictionFactor.establishedWorkPatterns',
    [EnergyPredictionFactorType.DEVELOPING_WORK_PATTERNS]: 'energyLevel.energyPredictionFactor.developingWorkPatterns',
    [EnergyPredictionFactorType.NEW_EMPLOYEE_PATTERNS]: 'energyLevel.energyPredictionFactor.newEmployeePatterns',
    [EnergyPredictionFactorType.HISTORICAL_ENERGY_DATA]: 'energyLevel.energyPredictionFactor.historicalEnergyData',
    [EnergyPredictionFactorType.LIMITED_DATA]: 'energyLevel.energyPredictionFactor.limitedData',
    [EnergyPredictionFactorType.TEMPORARILY_UNAVAILABLE]: 'energyLevel.energyPredictionFactor.temporarilyUnavailable',
    [EnergyPredictionFactorType.INSUFFICIENT_DATA]: 'energyLevel.energyPredictionFactor.insufficientData',
  }

  const messageId = factorMap[factor]
  return messageId ? formatMessage({ id: messageId }) : ''
}

/**
 * Generates custom factors based on suggestion data
 */
function getCustomFactors(suggestion: PTOSuggestion, formatMessage: IntlShape['formatMessage']): Array<{ id: string; text: string }> {
  const customFactors: Array<{ id: string; text: string }> = []

  // Check if holidays are included
  if (suggestion.holidaysIncluded && suggestion.holidaysIncluded.length > 0) {
    const holidayNames = suggestion.holidaysIncluded.map((h) => h.name).join(', ')
    customFactors.push({
      id: 'includesHolidays',
      text: formatMessage({ id: 'energyLevel.customFactor.includesHolidays' }, { holidayNames }),
    })
  }

  // Check weekend scenarios
  const hasWeekends = suggestion.weekendsIncluded && suggestion.weekendsIncluded.length > 0
  const ptoDaysUsed = suggestion.ptoDaysUsed || 0

  if (hasWeekends) {
    if (ptoDaysUsed === 1) {
      customFactors.push({
        id: 'longWeekendMinimalPTO',
        text: formatMessage({ id: 'energyLevel.customFactor.longWeekendMinimalPTO' }),
      })
    } else if (ptoDaysUsed > 1) {
      customFactors.push({
        id: 'extendsIntoWeekend',
        text: formatMessage({ id: 'energyLevel.customFactor.extendsIntoWeekend' }),
      })
    }
  }

  // Check PTO efficiency multiplier (only if more than 1 PTO day is used)
  if (ptoDaysUsed > 1) {
    const multiplier = suggestion.durationDays / ptoDaysUsed
    if (multiplier > 2) {
      const roundedMultiplier = Math.floor(multiplier)

      customFactors.push({
        id: 'maximumPTOEfficiency',
        text: formatMessage({ id: 'energyLevel.customFactor.maximumPTOEfficiency' }, { multiplier: roundedMultiplier.toString() }),
      })
    }
  }

  return customFactors
}

export const ActionDetails: React.FC<ActionDetailsProps> = ({ suggestion, onTooltipView }) => {
  const { formatMessage } = useIntl()
  const tooltipOpenTime = useRef<number | null>(null)
  const hasNoTeamConflicts = suggestion.overlappingUserNames?.length === 0

  const handleTooltipOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        tooltipOpenTime.current = Date.now()
      } else if (tooltipOpenTime.current && onTooltipView) {
        const hoverDurationMs = Date.now() - tooltipOpenTime.current
        const names = suggestion.overlappingUserNames?.join(', ') || ''
        onTooltipView('pto_suggestion', names, 'overlapping_teammates', 'suggestions', hoverDurationMs)
        tooltipOpenTime.current = null
      }
    },
    [onTooltipView, suggestion.overlappingUserNames]
  )
  const customFactors = getCustomFactors(suggestion, formatMessage)

  // Convert energy factors to the same format as custom factors
  const energyFactors = (suggestion.energyPrediction?.factors || [])
    .map((factor) => {
      const factorText = translateEnergyPredictionFactor(factor as EnergyPredictionFactorType, formatMessage)
      return factorText ? { id: `energy-${factor}`, text: factorText } : null
    })
    .filter((factor): factor is { id: string; text: string } => factor !== null)

  // Combine custom factors and energy factors, then slice to show first 3
  const allFactors = [...customFactors, ...energyFactors].slice(0, 3)

  return (
    <div className="action-details">
      {hasNoTeamConflicts ? (
        <div className="detail-item">
          <PiUsersLight className="detail-icon detail-icon-success" />
          <Text type="secondary">{formatMessage({ id: 'energyLevel.noTeamConflicts' })}</Text>
        </div>
      ) : (
        <div className="detail-item">
          <PiWarningBold className="detail-icon detail-icon-warning" />
          <Tooltip
            title={suggestion.overlappingUserNames && suggestion.overlappingUserNames.length > 0 ? suggestion.overlappingUserNames.join(', ') : undefined}
            placement="top"
            onOpenChange={handleTooltipOpenChange}
          >
            <Text type="secondary">
              {formatMessage(
                { id: 'energyLevel.teammateOff' },
                {
                  overlappingUsers: suggestion.overlappingUserNames?.length ?? 0,
                }
              )}
            </Text>
          </Tooltip>
        </div>
      )}

      {allFactors.map((factor) => (
        <div key={factor.id} className="detail-item">
          <PiCheckBold className="detail-icon detail-icon-success" />
          <Text type="secondary">{factor.text}</Text>
        </div>
      ))}
    </div>
  )
}
